import { getAuthToken } from "@app/relay/fetch-relay";

// A column's number-format hint. "text" (the default) writes a plain string;
// the others write a real number Excel can sum/sort/pivot. See Exports::TableXlsx.
export type XlsxColumnType = "text" | "money" | "percent" | "int" | "number";

export type XlsxColumn = { label: string; type?: XlsxColumnType };

// A cell is a scalar (formatted with its column's type) or a { v, t } pair that
// overrides the type for that one cell — used where a column mixes types (e.g. a
// percent margin row inside the money columns of the P&L).
export type XlsxCell =
  | string
  | number
  | null
  | { v: string | number | null; t: XlsxColumnType };

export type XlsxSheet = {
  sheetName: string;
  columns: XlsxColumn[];
  rows: XlsxCell[][];
  // Basis footnotes, written below the grid. Use these where a number needs a
  // caveat to travel with it (e.g. "movements are cash-basis").
  notes?: string[];
};

// One sheet inline, or a workbook of them. Both post to the same endpoint.
export type XlsxTable = { filename: string } & (
  | XlsxSheet
  | { sheets: XlsxSheet[] }
);

// POST the displayed grid to the backend XLSX writer and trigger a browser
// download of the returned file. The grid is exactly what the screen shows, so
// the file ties out to it. Authenticated with the same bearer token as GraphQL.
export async function exportTableToXlsx(table: XlsxTable): Promise<void> {
  const token = getAuthToken();
  const response = await fetch("/exports/table", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(table),
  });

  if (!response.ok) {
    throw new Error(`Export failed (${response.status})`);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${table.filename}.xlsx`;
  anchor.click();
  URL.revokeObjectURL(url);
}
